import PropTypes from 'prop-types'
import { useMemo } from 'react'
import { Text, DetailCard } from '@artprov/design-system'
import { TABLAS_LABELS, INDICADORES_INFO } from '../../../common/constants/labels'

/**
 * Componente para mostrar indicadore agrupados por tipo
 * @param {string} title - Título de la sección
 * @param {Object} data - Objeto con los datos de los tres tipos de indicadores
 * @param {boolean} isLoading - Estado de carga
 */

const formatPercentage = (value) => {
  if (value === null || value === undefined || value === '') return ''

  let formatted = String(value).replace('.', ',')

  if (formatted.startsWith(',')) {
    formatted = '0' + formatted
  }

  if (!formatted.endsWith('%')) {
    formatted += '%'
  }

  return formatted
}

const Indicadores = ({ title, data, isLoading }) => {
  const indicadoresFormateados = useMemo(() => {
    if (!data || isLoading) return []

    const resultado = []

    // Denuncias
    if (data.denuncias) {
      resultado.push({
        tipoId: 1,
        tipoDescripcion: 'Denuncias',
        indicadores: [
          {
            descripcion: 'Total de denuncias',
            valor: data.denuncias.totalDenuncias,
            tipo: 'totalDenuncias',
          },
          {
            descripcion: 'Autodenuncias',
            valor: data.denuncias.autodenuncias,
            tipo: 'autodenuncias',
          },
          {
            descripcion: 'Rechazos',
            valor: data.denuncias.rechazos,
            tipo: 'rechazos',
          },
        ],
      })
    }

    // Estado de los siniestros
    if (data.estado) {
      resultado.push({
        tipoId: 2,
        tipoDescripcion: 'Estado de los siniestros',
        indicadores: [
          {
            descripcion: 'Altas',
            valor: data.estado.altas,
            tipo: 'altas',
          },
          {
            descripcion: 'Recaídas',
            valor: data.estado.recaidas,
            tipo: 'recaidas',
          },
          {
            descripcion: 'Días de baja promedio',
            valor: data.estado.diasBajaPromedio,
            tipo: 'diasBajaPromedio',
          },
        ],
      })
    }

    // Indicadores frecuenciales
    if (data.frecuencia) {
      resultado.push({
        tipoId: 3,
        tipoDescripcion: 'Indicadores frecuenciales',
        indicadores: [
          {
            descripcion: 'Frecuencia siniestral',
            valor: formatPercentage(data.frecuencia.frecuenciaSiniestral),
            tipo: 'frecuenciaSiniestral',
          },
          {
            descripcion: 'Frecuencia judicial',
            valor: formatPercentage(data.frecuencia.frecuenciaJudicial),
            tipo: 'frecuenciaJudicial',
          },
          {
            descripcion: 'Frecuencia Siniestral por CIIU',
            valor: formatPercentage(data.frecuencia.frecuenciaSiniestralCiiu),
            tipo: 'frecuenciaSiniestralCiiu',
          },
        ],
      })
    }

    return resultado
  }, [data, isLoading])

  if (isLoading) {
    return (
      <div className="w-full mt-6">
        <Text variant="h7" className="mb-3 text-primary-10 font-bold">
          {title}
        </Text>
        <div className="rounded-md p-4 bg-tertiary-98 relative">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 lg:gap-6">
            {[0, 1, 2].map((col) => (
              <div key={col} className="flex flex-col gap-4">
                <div className="h-6 w-3/4 rounded bg-tertiary-80/30 animate-pulse" />
                <div className="flex flex-col gap-4">
                  {[0, 1, 2].map((row) => (
                    <div key={row} className="h-14 w-full rounded bg-tertiary-80/30 animate-pulse" />
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="absolute inset-0 flex items-center justify-center rounded-md">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-blue-primary" />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full mt-6">
      <Text variant="h7" className="mb-3 text-primary-10 font-semibold px-3">
        {title}
      </Text>
      <div className="rounded-md p-4 bg-tertiary-98">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 lg:gap-6">
          {indicadoresFormateados.map((grupo) => (
            <div key={grupo.tipoId} className="flex flex-col gap-4">
              <Text variant="body1" className="font-semibold text-tertiary-0 px-2">
                {grupo.tipoDescripcion}
              </Text>
              <div className="flex flex-col gap-4">
                {grupo.indicadores.length > 0 ? (
                  grupo.indicadores.map((indicador, index) => (
                    <DetailCard
                      key={`${grupo.tipoId}-${index}`}
                      title={indicador.descripcion}
                      content={indicador.valor}
                      showIcon={false}
                      infoMessage={INDICADORES_INFO[indicador.tipo]}
                      infoTooltipPosition="bottom-center"
                      showInfoIcon
                    />
                  ))
                ) : (
                  <div className="min-h-[5rem] flex items-center justify-center bg-tertiary-98 border border-tertiary-80 rounded-md px-3 py-2">
                    <Text variant="body2" className="text-tertiary-40 text-center">
                      <span className="font-bold">Sin datos</span> {TABLAS_LABELS.NO_DATA_PERIOD}
                    </Text>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

Indicadores.propTypes = {
  title: PropTypes.string.isRequired,
  data: PropTypes.shape({
    denuncias: PropTypes.shape({
      totalDenuncias: PropTypes.number,
      autodenuncias: PropTypes.number,
      rechazos: PropTypes.number,
    }),
    estado: PropTypes.shape({
      altas: PropTypes.number,
      recaidas: PropTypes.number,
      diasBajaPromedio: PropTypes.number,
    }),
    frecuencia: PropTypes.shape({
      frecuenciaSiniestral: PropTypes.number,
      frecuenciaJudicial: PropTypes.number,
      frecuenciaSiniestralCiiu: PropTypes.number,
    }),
  }).isRequired,
  isLoading: PropTypes.bool,
}

export default Indicadores
